import MoaPanel from "./moa_panel";
import { AcceptSubmitButton } from "./accept_submit_button";
import { MoaFormAnatomy, MOA_FORM_BASE_HTML_CLASS } from "../helpers/moa_panel_hints";
import { isNull, isUndefined } from "../../../helpers/object";

// AcceptForm — the primary "claim your card" panel (the default phase). Owns its own
// visibility, the email/name/hidden field management (prefill, blank, disabled
// states), the success title swap, the endpoint-response message (rendered into the
// hint slot), and the swapping footer: the "Rankers account? Sign in" pill (logged
// out) ⇄ the "Signed in as {name}. Sign out" identity row (signed in). Submit runs
// the host's offer-accept flow; the auth affordances delegate to the host.
//
// Note it does NOT show the shared authStatus — the accept flow has its own status
// channel (endpointStatus + acceptMessage), independent of the auth panels. Both live in
// the same state machine; "own channel" means different keys, not a different mechanism.
export const AcceptFormConfig = {
  componentTypeKey: "MoaAcceptForm",
  baseHtmlClass: MOA_FORM_BASE_HTML_CLASS,
  selector: ".member_offer_acceptor__form--accept",
  phase: "accept",

  successTitle: "Success! Check your email",

  titleSelector: MoaFormAnatomy.titleSelector,
  hintSelector: MoaFormAnatomy.hintSelector,

  // The hidden inputs carrying the server contract (their `name` attributes are fixed;
  // these classes are just our handle on them).
  idInputSelector: ".member_offer_acceptor__hidden_id",
  guidInputSelector: ".member_offer_acceptor__hidden_guid",
  memberOfferCodeInputSelector: ".member_offer_acceptor__hidden_member_offer_code",
  memberTierCodeInputSelector: ".member_offer_acceptor__hidden_member_tier_code",

  emailInputSelector: ".member_offer_acceptor__form__field--email input",
  nameInputSelector: ".member_offer_acceptor__form__field--name input",

  // Footer pills: the sign-in prompt and the identity row (mutually exclusive; the
  // host's --logged_in class swaps them), plus the Reset escape hatch shown only on a
  // signed-out success (CSS: --success:not(--logged_in)).
  signinButtonSelector: ".member_offer_acceptor__foot__item--signin button",
  identityLogoutButtonSelector: ".member_offer_acceptor__foot__item--identity button",
  identityNameSelector: ".member_offer_acceptor__foot__item--identity strong",
  resetButtonSelector: ".member_offer_acceptor__foot__item--reset button",
};

export class AcceptForm extends MoaPanel {
  static get config() { return AcceptFormConfig; }

  constructor(id_or_element, host) {
    super(id_or_element, host, AcceptFormConfig);

    this.titleEl = this.element.querySelector(AcceptFormConfig.titleSelector);
    this.hintEl = this.element.querySelector(AcceptFormConfig.hintSelector);
    this.emailInput = this.element.querySelector(AcceptFormConfig.emailInputSelector);
    this.nameInput = this.element.querySelector(AcceptFormConfig.nameInputSelector);
    this.signinButton = this.element.querySelector(AcceptFormConfig.signinButtonSelector);
    this.identityLogoutButton = this.element.querySelector(AcceptFormConfig.identityLogoutButtonSelector);
    this.identityNameEl = this.element.querySelector(AcceptFormConfig.identityNameSelector);
    this.resetButton = this.element.querySelector(AcceptFormConfig.resetButtonSelector);

    this.defaultTitle = this.titleEl.textContent;
    this.defaultHint = this.hintEl.textContent;

    // The Go button paints the endpoint state on itself (--loading / --success);
    // adopted so it is torn down with this panel.
    this.submitButton = this.adoptChildComponents(
      new AcceptSubmitButton(this.element.querySelector(AcceptSubmitButton.config.selector), this.host),
    );

    // The email the hidden identifiers (id/guid) belong to — i.e. whose acceptance
    // this browser currently owns. Seeded from the server-rendered fields; kept in
    // step by updateFormFields / blankFormFields. See startFreshAcceptanceIfEmailChanged.
    this.acceptanceEmail = this.emailInput.value;

    this.element.addEventListener("submit", this.handleSubmit.bind(this));
    this.signinButton.addEventListener("click", this.handleSignin.bind(this));
    this.identityLogoutButton.addEventListener("click", this.handleLogout.bind(this));
    this.resetButton.addEventListener("click", this.handleReset.bind(this));
  }

  // Accept's submit runs the host's offer-accept flow (not an auth intent).
  handleSubmit(event) {
    event.preventDefault();
    this.startFreshAcceptanceIfEmailChanged();
    this.host.submitAccept();
  }

  // The hidden id/guid identify the acceptance THIS BROWSER OWNS — the one the fields
  // were last filled from. A different email is a different claim (a second family
  // member on the same phone), so the identifiers must not carry over: the server
  // updates the acceptance it is given an id for, and for an already-accepted record
  // it ignores the new email and hands back the ORIGINAL person's success message —
  // so the new person would silently never get their card. Dropping the identifiers
  // makes the server create their own acceptance.
  startFreshAcceptanceIfEmailChanged() {
    if (sameEmail(this.emailInput.value, this.acceptanceEmail)) return;

    this.setInputValue(AcceptFormConfig.idInputSelector, "");
    this.setInputValue(AcceptFormConfig.guidInputSelector, "");
    this.acceptanceEmail = null; // submitAccept()'s new Submission becomes the owner
    this.dispatchAction("setAcceptMessage", null); // drop the previous claim's "already sent" / success note
  }

  handleSignin(event) {
    event.preventDefault();
    this.host.openSignIn();
  }

  handleLogout(event) {
    event.preventDefault();
    this.host.logoutSession();
  }

  // Only reachable on a signed-out success (the CSS shows the pill in that state
  // alone) — the counterpart to "Sign out" when there's no session to sign out of.
  handleReset(event) {
    event.preventDefault();
    this.host.resetAcceptance();
  }

  // The email the visitor has entered / had remembered on the accept form — carried
  // into the sign-in flow so they don't retype it.
  getEmail() { return this.emailInput.value; }

  updateFormFields(submission) {
    this.setInputValue(AcceptFormConfig.idInputSelector, submission.id);
    this.setInputValue(AcceptFormConfig.guidInputSelector, submission.guid);
    this.setInputValue(AcceptFormConfig.emailInputSelector, submission.email);
    this.setInputValue(AcceptFormConfig.memberOfferCodeInputSelector, submission.memberOfferCode);
    this.setInputValue(AcceptFormConfig.memberTierCodeInputSelector, submission.memberTierCode);
    this.setInputValue(AcceptFormConfig.nameInputSelector, submission.name);

    // The identifiers now belong to this submission's email.
    this.acceptanceEmail = submission.email;
  }

  // Reset the form to a FRESH acceptance. The hidden identifiers must go too: they
  // still carry the last submission's record, and the server updates an acceptance it
  // is given an id for. Leaving them would make the next accept — a different person
  // on a shared device, right after sign-out — overwrite the previous person's
  // MemberOfferAcceptance instead of creating their own.
  blankFormFields() {
    this.emailInput.value = "";
    this.nameInput.value = "";
    this.setInputValue(AcceptFormConfig.idInputSelector, "");
    this.setInputValue(AcceptFormConfig.guidInputSelector, "");
    this.acceptanceEmail = null;
  }

  setInputValue(selector, value) {
    const input = this.element.querySelector(selector);
    if (input) input.value = inputValue(value);
  }

  render() {
    this.renderPanelVisibility();

    // The email/name inputs are disabled on a successful accept (locked-in visual)
    // AND when signed in (the server fills them from current_person; disabling drops
    // them from HTML5 validation + FormSerialize). The Go button's own states live on
    // the AcceptSubmitButton subcomponent.
    const successful = !!this.host.submissionWasSuccessful();
    const loggedIn = !!this.readStateMachineValue("loggedIn");
    const person = this.readStateMachineValue("person");

    this.titleEl.textContent = successful ? AcceptFormConfig.successTitle : this.defaultTitle;

    // Signed in: fill the (disabled) email/name fields from the account identity so the
    // card visibly reflects the details it will be issued to. session_state supplies the
    // email; the name mirrors the server's card-name derivation (fullname || email =
    // displayName). Only while signed in — logged-out prefill comes from the localStorage
    // submissions store and must not be clobbered.
    //
    // Name-less exception: a signed-in account with no name yet (OTP-minted, person.name
    // null) has NO real name to show — displayName would be the email. So leave its name
    // field enabled and EMPTY, letting the visitor type a name that serializes and reaches
    // the server (member_offers_controller#accept lazy_set_names it onto the account).
    // The EMAIL field stays locked in both cases — the no-inline-redirect guard is unchanged.
    const nameless = loggedIn && person && !person.name;
    if (loggedIn && person) {
      this.emailInput.value = person.email || "";
      // Prefill the name ONLY for a NAMED account, where the field is disabled and this is a
      // display value. For a NAME-LESS account the field is enabled and the visitor is typing
      // into it — and render() runs on EVERY dispatch, including mid-submit (handleSubmit's
      // startFreshAcceptanceIfEmailChanged fires one). Writing the value here would wipe what
      // they typed before getFormParams() serializes it (empty: false then drops the field),
      // so the typed name never reaches the server. Leave the field to the visitor; its empty
      // start comes from the server markup, and sign-out clears it via blankFormFields().
      if (!nameless) this.nameInput.value = person.displayName || "";
    }

    this.emailInput.disabled = successful || loggedIn;
    this.nameInput.disabled = successful || (loggedIn && !nameless);

    this.identityNameEl.textContent = (person && person.displayName) || "";

    // The hint carries the endpoint-response message (or the server-rendered default)
    // — NOT the shared authStatus: the accept flow has its own status channel
    // (endpointStatus + acceptMessage), independent of the auth panels'. A REJECTED
    // submission's message is the server's validation errors ("Name can't be blank…"),
    // so it takes the same error tone the auth panels use — otherwise a refusal reads
    // like the neutral default copy.
    this.hintEl.classList.toggle(ACCEPT_HINT_ERROR_CLASS, !!this.host.submissionWasRejected());
    this.hintEl.textContent = this.readStateMachineValue("acceptMessage") || this.defaultHint;

    // Fan out, as the host does to the panels (the shared machine also repaints the
    // button on every dispatch; this covers the non-dispatch renders).
    this.submitButton.render();
  }
}

const ACCEPT_HINT_ERROR_CLASS = `${MOA_FORM_BASE_HTML_CLASS}__hint--error`;

function inputValue(v) {
  if (!isUndefined(v) && !isNull(v) && v !== "") return v;
  return "";
}

// Addresses are matched the way a mail server would: case- and whitespace-insensitive.
// Two blanks count as the same (nothing owns the identifiers either way).
function sameEmail(a, b) {
  return normalisedEmail(a) === normalisedEmail(b);
}

function normalisedEmail(v) {
  return inputValue(v).trim().toLowerCase();
}
